import { useEffect } from "react";

import isCrossOriginEmbedded from "@/embedIntegration/isCrossOriginEmbedded";
import { logger } from "@/monitoring/utils/logger";

import {
  type DGPreferenceCallbackParams,
  getDgBannerApi,
} from "../dgBannerApi";

/**
 * DataGrail exposes no way to unregister a preference callback, so registration
 * has to be idempotent against the window rather than per-mount. Without this a
 * StrictMode double-mount would register two handlers and log twice.
 */
const REGISTERED_FLAG = "__newselaConsentPersistenceCheckRegistered";

export const CONSENT_PERSISTENCE_WARNING =
  "DataGrail consent preferences did not persist after a consent change";

/** Reading a cross-origin parent's location throws. That throw is the answer. */
const getTopLevelOrigin = (): string => {
  try {
    return window.top?.location.origin ?? "unavailable";
  } catch {
    return "unavailable";
  }
};

/**
 * Reports when a consent choice fails to persist.
 *
 * The fix for USMAN-6287 rewrites DataGrail's cookie attributes from the outside,
 * in `partitionedCookieShim`. That works today, but it depends on DataGrail
 * continuing to write cookies through `document.cookie` under names we match. If
 * either changes, the shim stops working and the consent banner loop returns with
 * no signal at all. This is that signal.
 *
 * `getConsentPreferences().isCustomised` is the exact check. DataGrail returns
 * `true` only when it can read its preferences cookie back and parse it, so a
 * `false` immediately after a change means the write was dropped. Reading through
 * its API rather than `document.cookie` also handles the `_s` cookie suffix that
 * its `allCookieSubdomains` plugin adds.
 *
 * Warn, never error. A blocked third-party cookie is normal browser policy, not a
 * fault of ours, and nobody should be paged for it.
 */
const useConsentPersistenceCheck = (): void => {
  useEffect(() => {
    const globalScope = window as unknown as Record<string, unknown>;
    if (globalScope[REGISTERED_FLAG]) {
      return;
    }
    globalScope[REGISTERED_FLAG] = true;

    let hasWarned = false;

    const handlePreferenceChange = (
      params: DGPreferenceCallbackParams = {},
    ) => {
      if (hasWarned) {
        return;
      }

      // A page where the DataGrail env vars are unset has no API to ask.
      const preferences = getDgBannerApi()?.getConsentPreferences();
      if (!preferences || preferences.isCustomised) {
        return;
      }

      hasWarned = true;
      logger.warn({
        message: CONSENT_PERSISTENCE_WARNING,
        context: {
          isCrossOriginEmbedded: isCrossOriginEmbedded(),
          topLevelOrigin: getTopLevelOrigin(),
          action: params.action,
          policyName: params.policyName,
          hasStorageAccessApi: "requestStorageAccess" in document,
        },
      });
    };

    // Only `preference_callback`. On a first visit `initial_preference_callback`
    // fires with no stored choice, where `isCustomised: false` is correct rather
    // than a failure.
    window.dgEvent = window.dgEvent ?? [];
    window.dgEvent.push({
      event: "preference_callback",
      params: handlePreferenceChange,
    });
  }, []);
};

export default useConsentPersistenceCheck;
